import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import bgImage from "../assets/bg.png";

const OTPPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();

  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const email = location.state?.email;
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) {
      navigate("/login");
    }
  }, [email, navigate]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const completeOtp = otp.join("");

    if (completeOtp.length !== 6) {
      setError("Please enter the complete 6-digit code.");
      setLoading(false);
      return;
    }

    try {
      const response = await api.post("/auth/otp/verify/", {
        email,
        otp: completeOtp,
      });
      loginWithToken(response.data);
      navigate(response.data?.user?.is_first_login ? "/onboarding" : "/");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          "Verification failed. Please check the code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setResendLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post("/auth/otp/resend/", { email });
      setSuccess("A new OTP has been sent to your email.");
    } catch (err) {
      setError("Failed to resend OTP. Please try again in a moment.");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      {/* Background with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center z-0" 
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Showcase (Hidden on Mobile) */}
        <div className="hidden lg:block space-y-8 animate-fade-in">
          <div>
            <span className="text-chafadi-primary font-black uppercase tracking-[0.3em] text-[10px]">Verify your account</span>
            <h1 className="text-5xl font-black text-white mt-4 leading-tight font-display tracking-tight">
              Finalize synchronization with the inbox code.
            </h1>
            <p className="text-white/70 text-lg font-medium mt-6 max-w-md leading-relaxed uppercase tracking-widest text-xs">
              Execute 6-digit verification code to initialize profile onboarding and gain immediate marketplace authority.
            </p>
          </div>

          <div className="space-y-6 pt-8 border-t border-white/10">
            {[
              { title: "Instant Access", desc: "Verification logs you in and sends you to onboarding immediately." },
              { title: "Secure Protocol", desc: "High-speed delivery with resend support and multi-region compatibility." },
              { title: "Operational Ready", desc: "Verify and start browsing or posting assets in seconds." }
            ].map((item, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-6 h-6 rounded-full bg-chafadi-primary/20 flex items-center justify-center shrink-0 border border-chafadi-primary/30">
                  <div className="w-1.5 h-1.5 rounded-full bg-chafadi-primary shadow-[0_0_10px_#ff7a00] animate-pulse"></div>
                </div>
                <div>
                  <h4 className="text-white font-black text-[10px] uppercase tracking-widest">{item.title}</h4>
                  <p className="text-white/40 text-[9px] font-bold mt-1 leading-relaxed uppercase tracking-widest">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Verification Card */}
        <div className="w-full max-w-md mx-auto animate-zoom-in">
          <div className="bg-white rounded-[40px] p-8 md:p-12 shadow-3xl border border-white/20">
            <div className="text-center mb-10">
              <img src="/logo.png" alt="Chafadi" className="h-10 mx-auto mb-8" />
              <span className="inline-block px-4 py-1.5 bg-theme-bg text-[9px] font-black uppercase tracking-widest text-theme-muted rounded-full mb-4 border border-component-border shadow-inner">Identity Sync</span>
              <h2 className="text-2xl font-black text-theme-text mb-2 tracking-tight">Establish Handshake</h2>
              <p className="text-[10px] font-bold text-theme-muted uppercase tracking-widest">
                Target: <span className="text-chafadi-primary font-black">{email}</span>
              </p>
            </div>

            {error && (
              <div className="bg-rose-50 text-rose-500 p-5 rounded-2xl border border-rose-100 flex items-center gap-4 text-[9px] font-black uppercase tracking-widest mb-8 animate-shake">
                <FiAlertCircle className="text-lg shrink-0" />
                {error}
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 text-emerald-600 p-5 rounded-2xl border border-emerald-100 flex items-center gap-4 text-[9px] font-black uppercase tracking-widest mb-8">
                <FiCheckCircle className="text-lg shrink-0" />
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-10">
              <div className="grid grid-cols-6 gap-2 sm:gap-4">
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength="1"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full aspect-square bg-theme-bg border-2 border-transparent rounded-2xl text-center text-xl font-black text-theme-text focus:bg-white focus:border-chafadi-primary/30 outline-none transition-all shadow-inner"
                    value={data}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    ref={(el) => (inputRefs.current[index] = el)}
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-16 bg-theme-text text-white font-black rounded-full shadow-2xl hover:bg-black transition-all active:scale-[0.98] flex items-center justify-center disabled:opacity-50 uppercase tracking-[0.3em] text-xs pt-1"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  "Execute Verification"
                )}
              </button>
            </form>

            <div className="mt-10 text-center pt-8 border-t border-component-border border-dashed">
              <p className="text-[9px] font-black text-theme-muted mb-4 uppercase tracking-[0.2em] opacity-40 italic">Signal not received?</p>
              <button
                onClick={handleResendOTP}
                disabled={resendLoading}
                className="text-[10px] font-black text-chafadi-primary hover:underline transition-all disabled:opacity-50 uppercase tracking-[0.3em]"
              >
                {resendLoading ? (
                  <span className="flex items-center gap-3">
                    <div className="w-3 h-3 border-2 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin"></div>
                    BROADCASTING...
                  </span>
                ) : (
                  "Resend Pulse"
                )}
              </button>
            </div>
          </div>
          
          <p className="mt-10 text-center text-white/40 text-[9px] font-black uppercase tracking-[0.4em]">
            Global Secure Node &bull; Gen Alpha Trading
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPPage;
